import { InsuranceType, MainPath } from "Constants/constants";
import {
  editPriceCalculationRequest,
  sendPriceCalculationRequestStart,
} from "Store/priceCalculation/priceCalculationSlice";
import React, { ReactElement, Suspense, lazy, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Route, Router, Switch, useHistory } from "react-router-dom";
import {
  VALUABLE_PATH,
  ValuablesCheckPrice,
} from "./Products/RiskProducts/Valuables";

import { ThemedSpinner } from "Components/Themes/components/ThemedSpinner";
import { setCurrentAppPart } from "Store/analytics/analyticsSlice";
import { featureToggle } from "Store/featureToggles/featureToggleUtils";
import { InsuranceProductPriceRequestInfo } from "Store/interfaces/products/checkPrice";
import { usePartnerAgreementNumber } from "Store/partners/partnerHooks";
import { ReduxState } from "Store/rootReducer";
import { useTexts } from "Store/texts";
import { AgreementCode } from "Store/userIdentification/agreementCodes";
import { AppPartName } from "Utils/analytics";
import { UpdatePriceRequestProps } from "./Products/productInterfaces";
import { getNumberOfProductsCustomerWillHaveInCartIncludedThisOne } from "./checkPriceUtils";
import { getAgreementProduct } from "./helpers/getAgreementProduct";

const AccidentCheckPrice = lazy(
  () => import("./Products/RiskProducts/Accident"),
);
const Advice = lazy(() => import("./Products/Advice"));
const AkaFamilyAccidentCheckPrice = lazy(
  () => import("./Products/RiskProducts/AkaFamilyAccident"),
);
const AkaHealthCheckPrice = lazy(
  () => import("./Products/LifeProducts/AkaHealth"),
);
const AkaHouseContentsCheckPrice = lazy(
  () => import("./Products/RiskProducts/HouseProducts/AkaHouseContents"),
);
const AkaStudentCheckPrice = lazy(
  () => import("./Products/AkaProducts/AkaStudent"),
);
const AnimalCheckPrice = lazy(() => import("./Products/RiskProducts/Animal"));
const BoatCheckPrice = lazy(
  () => import("./Products/RiskProducts/VehicleProducts/Boat"),
);
const CabinCheckPrice = lazy(
  () => import("./Products/RiskProducts/HouseProducts/Cabin"),
);
const CabinContentsCheckPrice = lazy(
  () => import("./Products/RiskProducts/HouseProducts/CabinContents"),
);
const CaravanCheckPrice = lazy(
  () => import("./Products/RiskProducts/VehicleProducts/Caravan"),
);
const CarCheckPrice = lazy(
  () => import("./Products/RiskProducts/VehicleProducts/Car"),
);
const ChildCheckPrice = lazy(() => import("./Products/LifeProducts/Child"));
const AkaChildCheckPrice = lazy(
  () => import("./Products/LifeProducts/AkaChild"),
);
const ElectricScooterCheckPrice = lazy(
  () => import("./Products/RiskProducts/VehicleProducts/ElectricScooter"),
);
const HealthCheckPrice = lazy(() => import("./Products/LifeProducts/Health"));
const HouseCheckPrice = lazy(
  () => import("./Products/RiskProducts/HouseProducts/House/HouseCheckPrice"),
);
const HouseContentsCheckPrice = lazy(
  () => import("./Products/RiskProducts/HouseProducts/HouseContents"),
);
const HuseierneHouseContentsCheckPrice = lazy(
  () => import("./Products/RiskProducts/HouseProducts/HuseierneHouseContents"),
);
const MopedCheckPrice = lazy(
  () => import("./Products/RiskProducts/VehicleProducts/Moped"),
);
const MotorcycleCheckPrice = lazy(
  () => import("./Products/RiskProducts/VehicleProducts/Motorcycle"),
);
const PregnantAndNewborn = lazy(
  () => import("./Products/LifeProducts/PregnantAndNewborn"),
);
const Rental = lazy(
  () => import("./Products/RiskProducts/HouseProducts/Rental"),
);
const TractorCheckPrice = lazy(
  () => import("./Products/RiskProducts/VehicleProducts/Tractor"),
);
const TrailerCheckPrice = lazy(
  () => import("./Products/RiskProducts/VehicleProducts/Trailer"),
);
const TravelCheckPrice = lazy(() => import("./Products/RiskProducts/Travel"));
const AkaTravelCheckPrice = lazy(
  () => import("./Products/RiskProducts/AkaTravel"),
);
const SopSicknessLeaveCheckPrice = lazy(
  () => import("./Products/LifeProducts/SopSicknessLeave"),
);
const VeteranAutoCheckPrice = lazy(
  () => import("./Products/RiskProducts/VehicleProducts/VeteranAuto"),
);

const NoMatch = (): ReactElement => <Redirect to="/" />;

// eslint-disable-next-line sonarjs/cognitive-complexity
export const CheckPrice = (): React.ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const texts = useTexts();
  const partnerAgreementNumber = usePartnerAgreementNumber();
  const {
    userIdentification: { current: userIdentificationResponses },
    priceCalculation: { priceCalculationRequest },
    cart,
  } = useSelector((state: ReduxState) => state);
  const discountResponse = userIdentificationResponses?.discountResponse;
  const newCartResponse = userIdentificationResponses?.newCartResponse;
  const newCustomerResponse = userIdentificationResponses?.newCustomerResponse;
  const numberOfProductsInCart =
    getNumberOfProductsCustomerWillHaveInCartIncludedThisOne(cart.current);

  useEffect(() => {
    dispatch(setCurrentAppPart(AppPartName.CHECK_PRICE));
  }, []);

  const updatePriceRequestWithFormValues = ({
    productPriceUrl,
    stepForwardOnSuccess,
    updatedProductPriceRequestInfo,
    agreement,
    clearOldPriceCalculations = false,
    includeGender = false,
  }: UpdatePriceRequestProps): void => {
    dispatch(editPriceCalculationRequest(updatedProductPriceRequestInfo));
    if (discountResponse && newCartResponse && newCustomerResponse) {
      dispatch(
        sendPriceCalculationRequestStart({
          productPriceUrl,
          stepForwardOnSuccess,
          priceCalculationRequest: {
            productInfo: {
              isFemale: includeGender
                ? newCustomerResponse.isFemale
                : undefined,
              ...priceCalculationRequest.productInfo,
              ...updatedProductPriceRequestInfo,
              cartId: newCartResponse.cartId,
              dateOfBirth: newCustomerResponse.dateOfBirth,
              customerRef: newCustomerResponse.customerRef,
            } as InsuranceProductPriceRequestInfo, //TODO: Fix typing here
            discountInfo: {
              agreementProduct: getAgreementProduct({
                agreementProductOverwrite: agreement,
                manualAgreementProduct:
                  newCustomerResponse.manualAgreementProduct,
                partnerAgreement: partnerAgreementNumber,
                agreementProductFromDiscount:
                  discountResponse.f2100.agreementProduct,
              }),
              isNewInsuranceCustomer:
                discountResponse.f2100.isNewInsuranceCustomer,
              paymentPeriod: discountResponse.f2100.paymentPeriod,
            },
            numberOfProductsInCart: numberOfProductsInCart,
          },
          clearOldPriceCalculations,
        }),
      );
    }
  };

  const fetchRelatedProductPriceInformation = (
    productPriceUrl: string,
    updatedProductPriceRequestInfo: InsuranceProductPriceRequestInfo,
    clearOldPriceCalculations = false,
  ): void => {
    dispatch(editPriceCalculationRequest(updatedProductPriceRequestInfo));
    if (discountResponse && newCartResponse && newCustomerResponse) {
      dispatch(
        sendPriceCalculationRequestStart({
          productPriceUrl,
          priceCalculationRequest: {
            productInfo: {
              ...updatedProductPriceRequestInfo,
              cartId: newCartResponse.cartId,
              dateOfBirth: newCustomerResponse.dateOfBirth,
              customerRef: newCustomerResponse.customerRef,
            } as InsuranceProductPriceRequestInfo, //TODO: Fix typing here
            discountInfo: {
              agreementProduct: getAgreementProduct({
                manualAgreementProduct:
                  newCustomerResponse.manualAgreementProduct,
                partnerAgreement: partnerAgreementNumber,
                agreementProductFromDiscount:
                  discountResponse.f2100.agreementProduct,
              }),
              isNewInsuranceCustomer:
                discountResponse.f2100.isNewInsuranceCustomer,
              paymentPeriod: discountResponse.f2100.paymentPeriod,
            },
            numberOfProductsInCart: numberOfProductsInCart,
          },
          clearOldPriceCalculations,
        }),
      );
    }
  };

  if (!texts.fetched) {
    return <ThemedSpinner />;
  }

  return (
    <Router history={history}>
      <Switch>
        {process.env.NODE_ENV === "development" && (
          <Route exact path={`/${MainPath.CHECK_PRICE}/`}>
            <Redirect to="/" />
          </Route>
        )}
        {featureToggle(texts.domain.featureToggles.checkPriceAccident) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.ACCIDENT}`}
            render={() => (
              <AccidentCheckPrice
                updatePriceRequest={updatePriceRequestWithFormValues}
                sendRelatedPriceRequest={fetchRelatedProductPriceInformation}
              />
            )}
          />
        )}
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceAkaHealth,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.AKA_HEALTH}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <AkaHealthCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                  sendRelatedPriceRequest={fetchRelatedProductPriceInformation}
                />
              </Suspense>
            )}
          />
        )}
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceAkaStudent,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.AKA_STUDENT}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <AkaStudentCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                />
              </Suspense>
            )}
          />
        )}
        {featureToggle(texts.domain.featureToggles.checkPriceBoat) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.BOAT}`}
            render={() => (
              <BoatCheckPrice
                updatePriceRequest={updatePriceRequestWithFormValues}
                sendRelatedPriceRequest={fetchRelatedProductPriceInformation}
              />
            )}
          />
        )}
        <Route
          path={`/${MainPath.CHECK_PRICE}/${InsuranceType.CHILD}`}
          render={() => (
            <Suspense fallback={<ThemedSpinner />}>
              <ChildCheckPrice
                numberOfProductsInCart={numberOfProductsInCart}
              />
            </Suspense>
          )}
        />
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceAkaChild,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.AKA_CHILD}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <AkaChildCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                />
              </Suspense>
            )}
          />
        )}
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceAkaFamilyAccident,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.AKA_FAMILY_ACCIDENT}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <AkaFamilyAccidentCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                />
              </Suspense>
            )}
          />
        )}
        {featureToggle(texts.domain.checkPriceProductToggles.checkPriceCar) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.CAR}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <CarCheckPrice
                  numberOfProductsInCart={numberOfProductsInCart}
                />
              </Suspense>
            )}
          />
        )}
        <Route
          path={`/${MainPath.CHECK_PRICE}/${InsuranceType.CARAVAN}`}
          render={() => (
            <Suspense fallback={<ThemedSpinner />}>
              <CaravanCheckPrice
                updatePriceRequest={updatePriceRequestWithFormValues}
                sendRelatedPriceRequest={fetchRelatedProductPriceInformation}
              />
            </Suspense>
          )}
        />
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceElectricScooter,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.ELECTRIC_SCOOTER}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <ElectricScooterCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                  sendRelatedPriceRequest={fetchRelatedProductPriceInformation}
                />
              </Suspense>
            )}
          />
        )}
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceHealth,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.HEALTH}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <HealthCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                  sendRelatedPriceRequest={fetchRelatedProductPriceInformation}
                />
              </Suspense>
            )}
          />
        )}
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceHouse,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.HOUSE}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <HouseCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                  sendRelatedPriceRequest={fetchRelatedProductPriceInformation}
                />
              </Suspense>
            )}
          />
        )}
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceHouseContents,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.HOUSE_CONTENTS}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <HouseContentsCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                />
              </Suspense>
            )}
          />
        )}
        {featureToggle(
          texts.domain.checkPriceProductToggles
            .checkPriceHuseierneHouseContents,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.HUSEIERNE_HOUSE_CONTENTS}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <HuseierneHouseContentsCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                />
              </Suspense>
            )}
          />
        )}
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceAkaHouseContents,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.AKA_HOUSE_CONTENTS}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <AkaHouseContentsCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                />
              </Suspense>
            )}
          />
        )}
        <Route
          path={`/${MainPath.CHECK_PRICE}/${InsuranceType.TRAVEL}`}
          render={() => (
            <Suspense fallback={<ThemedSpinner />}>
              <TravelCheckPrice
                updatePriceRequest={updatePriceRequestWithFormValues}
              />
            </Suspense>
          )}
        />
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceAkaTravel,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.AKA_TRAVEL}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <AkaTravelCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                />
              </Suspense>
            )}
          />
        )}
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceAdvice,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.ADVICE}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <Advice />
              </Suspense>
            )}
          />
        )}
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceCabin,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.CABIN}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <CabinCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                  sendRelatedPriceRequest={fetchRelatedProductPriceInformation}
                />
              </Suspense>
            )}
          />
        )}
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceCabinContents,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.CABIN_CONTENTS}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <CabinContentsCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                />
              </Suspense>
            )}
          />
        )}
        <Route
          path={`/${MainPath.CHECK_PRICE}/${InsuranceType.PREGNANT_AND_NEWBORN}`}
          render={() => (
            <Suspense fallback={<ThemedSpinner />}>
              <PregnantAndNewborn
                updatePriceRequest={(
                  productPriceUrl,
                  stepForwardOnSuccess,
                  updatedProductPriceRequestInfo,
                ) =>
                  updatePriceRequestWithFormValues({
                    productPriceUrl: productPriceUrl,
                    stepForwardOnSuccess,
                    updatedProductPriceRequestInfo:
                      updatedProductPriceRequestInfo,
                    agreement: AgreementCode.NORMAL_TARIFF,
                  })
                }
              />
            </Suspense>
          )}
        />
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceRental,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.RENTAL}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <Rental updatePriceRequest={updatePriceRequestWithFormValues} />
              </Suspense>
            )}
          />
        )}
        <Route
          path={`/${MainPath.CHECK_PRICE}/${InsuranceType.TRACTOR}`}
          render={() => (
            <Suspense fallback={<ThemedSpinner />}>
              <TractorCheckPrice
                numberOfProductsInCart={numberOfProductsInCart}
              />
            </Suspense>
          )}
        />
        <Route
          path={`/${MainPath.CHECK_PRICE}/${InsuranceType.TRAILER}`}
          render={() => (
            <Suspense fallback={<ThemedSpinner />}>
              <TrailerCheckPrice
                updatePriceRequest={updatePriceRequestWithFormValues}
              />
            </Suspense>
          )}
        />
        <Route
          path={`/${MainPath.CHECK_PRICE}/${InsuranceType.MOPED}`}
          render={() => (
            <Suspense fallback={<ThemedSpinner />}>
              <MopedCheckPrice
                updatePriceRequest={updatePriceRequestWithFormValues}
              />
            </Suspense>
          )}
        />
        <Route
          path={`/${MainPath.CHECK_PRICE}/${InsuranceType.MOTORCYCLE}`}
          render={() => (
            <Suspense fallback={<ThemedSpinner />}>
              <MotorcycleCheckPrice
                updatePriceRequest={updatePriceRequestWithFormValues}
              />
            </Suspense>
          )}
        />
        <Route
          path={VALUABLE_PATH}
          render={() => (
            <Suspense fallback={<ThemedSpinner />}>
              <ValuablesCheckPrice
                numberOfProductsInCart={numberOfProductsInCart}
              />
            </Suspense>
          )}
        />
        <Route
          path={`/${MainPath.CHECK_PRICE}/${InsuranceType.VETERAN_AUTO}`}
          render={() => (
            <Suspense fallback={<ThemedSpinner />}>
              <VeteranAutoCheckPrice
                numberOfProductsInCart={numberOfProductsInCart}
              />
            </Suspense>
          )}
        />
        {featureToggle(
          texts.domain.checkPriceProductToggles.checkPriceAnimal,
        ) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.ANIMAL}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <AnimalCheckPrice
                  numberOfProductsInCart={numberOfProductsInCart}
                />
              </Suspense>
            )}
          />
        )}
        {featureToggle(texts.domain.checkPriceProductToggles.checkPriceSop) && (
          <Route
            path={`/${MainPath.CHECK_PRICE}/${InsuranceType.SOP_SICKNESS_LEAVE}`}
            render={() => (
              <Suspense fallback={<ThemedSpinner />}>
                <SopSicknessLeaveCheckPrice
                  updatePriceRequest={updatePriceRequestWithFormValues}
                />
              </Suspense>
            )}
          />
        )}
        <Route component={NoMatch} />
      </Switch>
    </Router>
  );
};
